import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { CheckCircle2, AlertCircle, Check } from "lucide-react";

export default function AdvertisePage() {
    const brand = useBrand();
    const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", budget: "", message: "" });
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/advertise", form);
            if (data.success) setDone(true);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="advertise-page">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
                <div className="lg:pt-4">
                    <span className="eyebrow text-accent">Partnerships</span>
                    <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-[-0.035em] mt-3 leading-[1.08]">
                        Advertise with {brand.site_name}
                    </h1>
                    <p className="mt-6 text-base text-dim leading-relaxed">
                        Reach an engaged audience of investors, business owners and money-minded readers.
                        Tell us about your campaign and our partnerships team will send our media kit and rates.
                    </p>
                    <div className="mt-10 space-y-3">
                        {[
                            "Premium homepage and section placements",
                            "Newsletter sponsorships (email)",
                            "Native and sponsored editorial",
                            "SMS broadcast campaigns",
                        ].map((x) => (
                            <div key={x} className="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3">
                                <span className="grid place-items-center w-5 h-5 rounded-full bg-accent/15 text-accent shrink-0">
                                    <Check size={12} strokeWidth={3} />
                                </span>
                                <span className="text-sm text-dim">{x}</span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 font-mono text-xs text-dim">Or email us directly at {brand.email}</p>
                </div>

                <div className="panel p-7 sm:p-8">
                    {done ? (
                        <div className="flex flex-col items-center text-center py-12" data-testid="advertise-success">
                            <span className="grid place-items-center w-16 h-16 rounded-full bg-pos/10 text-pos">
                                <CheckCircle2 size={30} />
                            </span>
                            <h2 className="font-display text-xl font-bold tracking-[-0.02em] mt-5">Enquiry received</h2>
                            <p className="mt-3 text-sm text-dim leading-relaxed">
                                Thank you. Our partnerships team will be in touch shortly.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <h2 className="font-display text-xl font-bold tracking-[-0.02em] mb-5">Enquiry form</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="advertise-name" className="field" />
                                <input required placeholder="Company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="advertise-company" className="field" />
                            </div>
                            <input type="email" required placeholder="Work email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="advertise-email" className="field" />
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="advertise-phone" className="field" />
                                <input placeholder="Budget (optional)" value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="advertise-budget" className="field" />
                            </div>
                            <textarea
                                required
                                rows={4}
                                placeholder="Tell us about your campaign"
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                                data-testid="advertise-message"
                                className="field resize-none"
                            />
                            {error && (
                                <p
                                    className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg"
                                    data-testid="advertise-error"
                                >
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="advertise-submit"
                                className="w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-110 hover:shadow-glow transition-[filter,box-shadow] duration-150 disabled:opacity-50 disabled:hover:shadow-none"
                            >
                                {loading ? "Sending…" : "Send enquiry"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
